/**
 * App-store screenshot scenes. Each slug gets its own page at `/mobile/sliding/screenshot/:slug`.
 *
 * `storyId` — same UUID as `/puzzle/play-sliding/:storyId`.
 * `slidingId` — optional; same UUID as `.../board/:slidingId` (preferred — grid & difficulty come from API).
 * `expectedGrid` / `expectedLevel` — optional fallback when `slidingId` is omitted.
 * `promoHeadline` / `promoSubtitle` — Title Case headline + sentence subtitle above the board.
 * `label` — tag/category on the screenshot index.
 */

const SCENE_DEFAULTS = {
    slidingId: null,
    expectedGrid: null,
    expectedLevel: null,
    layout: "singleBoard",
    /** Store marketing look: classic | giant | themes | null (default shell). */
    marketing: null,
    /** Hide Quick Moves / Smooth Sliding pills. */
    hidePills: false,
    /** Hide story title + tag chips under the board. */
    hideStoryMeta: false,
    /** Hide grid-size / difficulty bar under the board. */
    hideSizeBar: false,
    galleryTag: null,
    galleryCount: 6,
    galleryTopCount: 0,
    /** Gallery grid columns (2 default; 4 for denser theme walls). */
    galleryColumns: 2,
    /** Optional stories pinned above the browse grid (shown in the top row). */
    galleryPrepend: null,
    /** Draw 4×4 (or board) grid overlay on the original photo. */
    showGridLines: false,
};

/** New screenshot content — original story photos (not scrambled boards). */
const SHOT_01 = {
    storyId: "1c73f525-e718-45ba-8e0b-313105045d0e",
    slidingId: "0d2f2455-507f-444f-b849-9fa5b3f8b39a",
};
const SHOT_02 = {
    storyId: "df3439ae-8d85-4592-9990-ea83b1ad8684",
    slidingId: "db6dbe3a-42ce-4863-b5d8-d4b787288bd6",
};
const SHOT_03 = {
    storyId: "95d97fda-f1ba-4b65-956f-1ba6d1018e88",
    slidingId: "16f5613c-1a95-471b-bd13-64b4983e0f10",
};
const SHOT_04 = {
    storyId: "6319355f-a016-4327-ad8c-26a69125e51b",
    slidingId: "697bb97d-b7b8-42ba-91b2-0f691b5394de",
};
const SHOT_05 = {
    storyId: "1c64f2a5-9c4e-4660-803a-44ee26de012d",
    slidingId: "7a801806-587c-44ec-afb8-12e474c06e22",
};
const SHOT_06 = {
    storyId: "efc1881d-c301-49cf-b6d4-26c65b225267",
    slidingId: "b69cb7a8-9d6e-4700-b21c-f2aa9c7baaf8",
};
const GALLERY_EXTRA = [
    { storyId: "62351d08-0f24-4735-b875-5847e0206dce" },
    { storyId: "c7f14324-6e52-4fad-b6ec-1689f4a0a05e" },
    { storyId: "fdbf9c46-147a-4eb5-ade3-03c30a265e20" },
];

/** Covers also used on jigsaw screenshots 07–08. */
const JIGSAW_GALLERY_COVERS = [
    { storyId: "cafc66f9-3660-456d-b960-b9c3929ec085" },
    { storyId: "c44b557b-34c5-4c23-a54b-e34bfe1057d4" },
    { storyId: "796fe431-d09f-4017-af1e-76e578c8b2f6" },
    { storyId: "21d9a9c1-5eec-4b51-b20e-d18c7a0689c8" },
    { storyId: "be5a1014-a4da-44a9-901b-460f413cd999" },
    { storyId: "df260da4-4003-4359-830e-539efafd77dc" },
];

const NEW_GALLERY_COVERS = [
    ...JIGSAW_GALLERY_COVERS,
    SHOT_01,
    SHOT_02,
    SHOT_03,
    SHOT_04,
    SHOT_05,
    SHOT_06,
    ...GALLERY_EXTRA,
];

export const SLIDING_SCREENSHOT_SCENES = [
    {
        ...SCENE_DEFAULTS,
        slug: "01",
        label: "Home",
        ...SHOT_01,
        layout: "home",
        marketing: "classic",
        hidePills: true,
        hideStoryMeta: true,
        showGridLines: true,
        promoHeadline: "Classic 4×4 Puzzles",
        promoSubtitle: "Clean grids. Smooth slides. Pure focus.",
    },
    {
        ...SCENE_DEFAULTS,
        slug: "02",
        label: "Interior",
        ...SHOT_02,
        showGridLines: true,
        promoHeadline: "Stylish Interiors",
        promoSubtitle: "Beautiful furniture and home interiors.",
    },
    {
        ...SCENE_DEFAULTS,
        slug: "03",
        label: "Numbers",
        ...SHOT_05,
        tileMode: "numbers",
        showGridLines: false,
        promoHeadline: "Number Mode",
        promoSubtitle: "Switch each tile to 1, 2, 3… and slide them home.",
    },
    {
        ...SCENE_DEFAULTS,
        slug: "04",
        label: "Giant 6×6",
        ...SHOT_04,
        /** Same layout as /03 (number board + promo); 6×6 from the story, not the 4×4 slidingId. */
        slidingId: null,
        theme: "light",
        tileMode: "numbers",
        expectedGrid: 6,
        expectedLevel: "HARD",
        showGridLines: false,
        promoHeadline: "GIANT 6x6 EXPERT GRID!",
        promoSubtitle: "Test your skill with 35 numbered tiles in complex motion.",
    },
    {
        ...SCENE_DEFAULTS,
        slug: "05",
        label: "Play",
        ...SHOT_03,
        storyId: "2dac3787-6312-4c26-b561-3fed6865baa1",
        slidingId: null,
        expectedGrid: 4,
        expectedLevel: "HARD",
        /** Scrambled 4×4 with arrows on the tiles beside the empty cell. */
        layout: "playBoard",
        theme: "light",
        hidePills: true,
        hideStoryMeta: true,
        hideSizeBar: false,
        showGridLines: false,
        promoHeadline: "Smooth Move",
        promoSubtitle: "Click the tile next to the empty cell. It slides in smoothly.",
    },
    {
        ...SCENE_DEFAULTS,
        slug: "06",
        label: "Puzzles",
        layout: "puzzles",
        marketing: "themes",
        galleryTag: null,
        galleryColumns: 2,
        gallerySquare: true,
        galleryTopCount: 0,
        galleryCount: 6,
        galleryPrepend: NEW_GALLERY_COVERS,
        ...SHOT_01,
        promoHeadline: "1000+ Puzzles",
        promoSubtitle: "A huge collection of stunning themes to play.",
    },
    {
        ...SCENE_DEFAULTS,
        slug: "07",
        label: "Travel",
        ...SHOT_06,
        storyId: "4b6f702a-34d0-4717-aaa6-feb80b52eb3d",
        slidingId: "4cb417fb-f83a-4977-a8a8-070b2888603e",
        expectedGrid: 6,
        /** Same play layout as /05: scrambled board only. */
        layout: "playBoard",
        theme: "light",
        hidePills: true,
        hideStoryMeta: true,
        hideSizeBar: false,
        showGridLines: false,
        promoHeadline: "Explore the World",
        promoSubtitle: "Beautiful scenes from around the globe.",
    },
    {
        ...SCENE_DEFAULTS,
        slug: "08",
        label: "Gorgeous",
        ...SHOT_04,
        storyId: "92f58ac4-737a-4be5-ba2f-ee1a2ed58846",
        slidingId: "d1c1e1ab-6844-49e1-93e6-24c9879b32db",
        showGridLines: true,
        promoHeadline: "Stunning Views",
        promoSubtitle: "Gorgeous sliding puzzles to solve.",
    },
];

/** @param {string} slug */
export function getScreenshotScene(slug) {
    return SLIDING_SCREENSHOT_SCENES.find((s) => s.slug === slug) ?? null;
}

/** True when the scene has a story id and can be opened. */
export function isScreenshotSceneConfigured(scene) {
    return Boolean(scene?.storyId && String(scene.storyId).trim());
}

/** Scenes that show a two-column gallery instead of one full-width board. */
export function isTwoColumnGalleryScene(scene) {
    return scene?.layout === "twoColumnGrid";
}
